import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform, LoadedImage, base64ToFile } from 'ngx-image-cropper';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { User } from '../../../shared/models/user.model';
import { MatDialogRef } from '@angular/material/dialog';
import { UserService } from '@app/admin-station/work-center/user/services/user.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { FileTypeEnum } from '../file-upload/constants';
import { ImageService } from '@app/shared/services/images/image.service';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { BasicButtonColors, BasicButtonModes } from '@app/shared/enums/buttons';
import { IUser } from '@app/shared/interfaces/user';

@Component({
  selector: 'cm-image-cropping',
  templateUrl: './image-cropping.component.html',
  styleUrls: ['./image-cropping.component.scss'],
})
export class ImageCroppingComponent implements OnInit, AfterViewInit {
  @ViewChild('imageUrl') imageUrl: ElementRef;
  @ViewChild(ImageCropperComponent) imageCropper: ImageCropperComponent;
  @ViewChild('fileUpload') fileUpload: FileUploadComponent;
  @Output() sideNavCloseEmmiter = new EventEmitter<void>();
  @Input() isSideNavStyle = false;

  public BasicButtonModes = BasicButtonModes;
  public BasicButtonsColor = BasicButtonColors;
  public removeAfterUpload = false;
  public cropImage = true;
  public uploadFileComp = false;

  public form: FormGroup;
  public flag = false;
  public loading = false;
  public user: User;
  public userId = '';
  public title = 'image-cropper.title';
  public profileImageUrl = '';
  public fallbackUrl = '';
  public autoCrop = false;
  public showCropperByUrl = false;

  public currentValue = 0;
  public imageChangedEvent: any = '';
  public croppedImage: any = '';
  public croppedObject: any;
  public showCropper = false;
  public zoom = 1;
  public rotation = 0;
  public FileTypeEnum = FileTypeEnum;
  public transform: ImageTransform = {
    translateUnit: 'px',
  };
  public imageLoadedValue = true;
  public isEditingPhoto = false;
  public userInfo: IUser = null;
  public currentFileExtension: string = null;

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private dialogRef: MatDialogRef<ImageCroppingComponent>,
    private formBuilder: FormBuilder,
    private _imageService: ImageService
  ) {
    this.userId = this.authService.getUserID();
  }

  ngAfterViewInit(): void {
    this.uploadFileComp = true;
  }

  ngOnInit(): void {
    this.loadImage();
    this.initForm();
  }

  public loadImage() {
    this.userService.getUserById(this.userId).then((userInfo) => {
      this.userInfo = userInfo;
      this._imageService.getAuthenticatedUrl(userInfo?.imageUrl).subscribe((authUrl) => {
        this.profileImageUrl = authUrl.authUrl;
        this.fallbackUrl = authUrl.fallbackUrl;
      });
    });
  }

  public removeCropImage() {
    this.imageChangedEvent = null;
    this.profileImageUrl = '';
  }

  public initForm(): void {
    this.form = this.formBuilder.group({
      images: ['', Validators.required],
    });
  }

  get imageURI(): string {
    return this.form.get('images').value[0].url;
  }

  setLoading(loading: boolean): void {
    this.loading = loading;
  }

  setImage(image): void {
    const images = [...(this.form.value.images || []), image];
    this.form.patchValue({ images });
    this.imageChangedEvent = image;
  }

  removeImage(imageIndex): void {
    const images = (this.form.value.images || []).filter((image, index) => index !== imageIndex);
    this.form.patchValue({ images });
  }

  async uploadImage(imageUrl): Promise<void> {
    await this.userService
      .getUserById(this.userId)
      .then(async (userInfo) => {
        const userUpdated = Object.assign({}, userInfo);
        userUpdated.imageUrl = imageUrl;
        return this.userService.updateUser(userInfo, userUpdated).then((result) => result.subscribe());
      })
      .then(() => {
        if (this.isSideNavStyle) {
          this.sideNavCloseEmmiter.emit();
        } else {
          this.dialogRef.close();
        }
      })
      .catch((error) => {
        this.title = 'image-cropper.title';
      })
      .finally(() => {
        this.loading = false;
      });
  }

  fileChangeEvent(event: any): void {
    this.imageChangedEvent = event;
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }
  imageLoaded(): void {
    setTimeout(() => {
      this.imageLoadedValue = true;
      this.autoCrop = true;
    }, 700);
  }
  cropperReady(): void {
    if (!this.autoCrop) this.imageCropper.crop();
  }

  getZoomSliderTickInterval(): void {
    this.transform = {
      ...this.transform,
      scale: this.zoom,
    };
  }

  getRotationSliderTickInterval(): void {
    this.transform = {
      ...this.transform,
      rotate: this.rotation,
    };
  }

  resetImageTransform(): void {
    this.zoom = 1;
    this.rotation = 0;
    this.transform = {
      ...this.transform,
      rotate: this.rotation,
      scale: this.zoom,
    };
  }

  uploadCroppedImage(): void {
    if(this.imageChangedEvent) {
      const File = this.base64ToFile(
        this.croppedImage,
        this.imageChangedEvent.target?.files[0].name ?? `edited-photo.${this.currentFileExtension}`
      );
      this.fileUpload.fileEvent({
        target: {
          files: [File],
        },
        cropImage: false,
        removeAfterUpload: true,
      });
    } else {
        this.uploadImage('');
    }
  }

  editProfilePicture(): void {
    this.imageChangedEvent = new Event('change');
    this.showCropperByUrl = true;
    this.isEditingPhoto = true;
    const split = (this.userInfo.imageUrl as string).split('/');
    const fileName = split[split.length - 1];
    this.currentFileExtension = fileName.split('.')[1];
  }

  base64ToFile(data, filename): File {
    const arr = data.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  public handleImageError(event: any) {
    const errorUrl = event.target.src;
    if (errorUrl.includes('resizedimages')) {
      this.profileImageUrl = this.fallbackUrl;
    } else {
      this.profileImageUrl = null;
    }
  }
}
